import { ReactNode, useEffect, useRef, useState } from 'react';
import Big from 'big.js';
import { ComboboxRef } from 'medere-ui-framework';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputNumber } from 'primereact/inputnumber';
import { ApiErrorResponse, INSUFFICIENT_BALANCE, parseApiError } from '../../services/apiError';
import { ComboDataDTO } from '../../dto/ComboDataDTO';
import { NO_OPEN_CASH_REGISTER_MESSAGE, usePaymentOrigin } from '../../hooks/usePaymentOrigin';
import useNotify from '../../hooks/useNotify';
import { formatMoney } from '../../utils/money';
import FieldError from '../FieldError/FieldError';
import PaymentOriginFields, { PaymentOriginField } from '../PaymentOriginFields/PaymentOriginFields';
import PaymentOriginNote from '../PaymentOriginFields/PaymentOriginNote';
import './ProfessionalPaymentDialog.scss';

type FieldName = 'amount' | PaymentOriginField;
type FieldErrors = Partial<Record<FieldName, string>>;

/** Orden visual de los campos: el foco va al primero con error. */
const FIELD_ORDER: FieldName[] = ['amount', 'paymentMethod', 'cashRegister', 'financialAccount'];

/** Campo del request (rendición o liquidación) → campo del formulario, para los marcadores que manda el backend. */
const REQUEST_FIELDS: Record<string, FieldName> = {
    amount: 'amount',
    paymentAmount: 'amount',
    paymentMethodId: 'paymentMethod',
    locationCashRegisterId: 'cashRegister',
    financialAccountId: 'financialAccount',
    paymentOrderFinancialAccountId: 'financialAccount',
};

/** Pago que ya pasó la validación del formulario. */
export interface ProfessionalPaymentValues {
    amount: number;
    paymentMethodId: number;
    /** Las dos en null si el sitio no maneja cajas. */
    locationCashRegisterId: number | null;
    financialAccountId: number | null;
}

interface ProfessionalPaymentDialogProps {
    visible: boolean;
    header: string;
    /** Datos de solo lectura arriba del formulario (cierre, profesional, saldo pendiente…). */
    summary: ReactNode;
    /** Lo que falta pagar: precarga el monto y es su tope. */
    pendingAmount: number;
    /** Aclaración opcional debajo del resumen (por ejemplo, a qué comprobantes se aplica el pago). */
    note?: ReactNode;
    /** Registra el pago en el backend. Si falla, el diálogo muestra el error en el campo o como aviso. */
    onSubmit: (payment: ProfessionalPaymentValues) => Promise<void>;
    onHide: () => void;
    onRegistered: () => void;
}

/**
 * Pago a un profesional, igual en la rendición (pago adicional) y en la liquidación: monto con tope en lo pendiente
 * y origen del pago (medio, caja abierta del usuario, cuenta financiera y su saldo). Valida por campo (DS §3.8.1).
 */
export default function ProfessionalPaymentDialog({
    visible,
    header,
    summary,
    pendingAmount,
    note,
    onSubmit,
    onHide,
    onRegistered,
}: ProfessionalPaymentDialogProps) {
    const notify = useNotify();

    const [amount, setAmount] = useState<number | null>(null);
    const [paymentMethod, setPaymentMethod] = useState<ComboDataDTO | null>(null);
    const [errors, setErrors] = useState<FieldErrors>({});
    const [submitting, setSubmitting] = useState(false);

    const origin = usePaymentOrigin({ enabled: visible, paymentMethodId: paymentMethod?.id });
    const resetOrigin = origin.reset;

    const amountRef = useRef<HTMLInputElement>(null);
    const paymentMethodRef = useRef<ComboboxRef>(null);
    const cashRegisterRef = useRef<ComboboxRef>(null);
    const financialAccountRef = useRef<ComboboxRef>(null);

    useEffect(() => {
        if (!visible) return;
        setAmount(pendingAmount);
        setPaymentMethod(null);
        setErrors({});
        resetOrigin();
    }, [visible, pendingAmount, resetOrigin]);

    function clearError(field: FieldName) {
        setErrors(current => {
            if (!current[field]) return current;
            const { [field]: _removed, ...rest } = current;
            return rest;
        });
    }

    function focusField(field: FieldName) {
        if (field === 'amount') amountRef.current?.focus();
        if (field === 'paymentMethod') paymentMethodRef.current?.focus();
        if (field === 'cashRegister') cashRegisterRef.current?.focus();
        if (field === 'financialAccount') financialAccountRef.current?.focus();
    }

    function showErrors(fieldErrors: FieldErrors) {
        setErrors(fieldErrors);
        const first = FIELD_ORDER.find(field => fieldErrors[field]);
        if (first) focusField(first);
        return first != null;
    }

    function validate(): FieldErrors {
        const result: FieldErrors = {};

        if (amount == null || amount <= 0) {
            result.amount = 'Ingresá el monto a pagar';
        } else if (new Big(amount).gt(pendingAmount)) {
            result.amount = `Ingresá un monto de hasta ${formatMoney(pendingAmount)} (lo pendiente)`;
        } else if (origin.requiresPaymentOrigin && origin.balance != null && new Big(amount).gt(origin.balance)) {
            result.amount = `Ingresá un monto de hasta ${formatMoney(origin.balance)} (el saldo disponible)`;
        }
        if (!paymentMethod) result.paymentMethod = 'Seleccioná el medio de pago';
        // Sin cajas en el sitio no se piden caja ni cuenta: el pago se registra sin esos movimientos
        if (origin.requiresPaymentOrigin) {
            if (!origin.cashRegister) result.cashRegister = 'Seleccioná la caja de origen';
            if (!origin.financialAccount) {
                result.financialAccount = 'Seleccioná la cuenta financiera';
            } else if (origin.balance == null) {
                result.financialAccount = 'Volvé a seleccionar la cuenta para consultar su saldo';
            }
        }
        return result;
    }

    /** Los errores del backend que apuntan a un campo van como marcador; el resto, como aviso. */
    function fieldErrorsFrom(apiError: ApiErrorResponse): FieldErrors {
        const result: FieldErrors = {};
        for (const detail of apiError.details ?? []) {
            const field = REQUEST_FIELDS[detail.field];
            if (field && !result[field]) result[field] = detail.message;
        }
        if (apiError.errorCode === INSUFFICIENT_BALANCE && !result.amount) result.amount = apiError.message;
        return result;
    }

    async function handleSubmit() {
        // Sin saber si el sitio maneja cajas no se registra: podría quedar un egreso sin su movimiento de caja
        if (origin.cashRegistersStatus !== 'loaded' || origin.missingOpenCashRegister) return;
        if (showErrors(validate())) return;

        setSubmitting(true);
        try {
            await onSubmit({
                amount: amount!,
                paymentMethodId: paymentMethod!.id,
                locationCashRegisterId: origin.requiresPaymentOrigin ? origin.cashRegister!.locationCashRegisterId : null,
                financialAccountId: origin.requiresPaymentOrigin ? origin.financialAccount!.financialAccountId : null,
            });
            notify.success('Pago registrado');
            onRegistered();
        } catch (error: unknown) {
            const apiError = parseApiError(error);
            if (showErrors(fieldErrorsFrom(apiError))) return;
            // Sesión de caja, comprobantes…: el backend manda el motivo escrito para el usuario
            const isBusinessError = apiError.status >= 400 && apiError.status < 500;
            notify.error(isBusinessError ? apiError.message : 'No se pudo registrar el pago. Intentá de nuevo.');
        } finally {
            setSubmitting(false);
        }
    }

    function handleHide() {
        if (!submitting) onHide();
    }

    const footer = (
        <div className="md-dialog__footer">
            <Button outlined label="Cancelar" className="md-ghost" onClick={handleHide} disabled={submitting} />
            <Button
                label="Registrar pago"
                className="md-primary"
                onClick={handleSubmit}
                loading={submitting || (origin.requiresPaymentOrigin && origin.loadingBalance)}
                disabled={origin.cashRegistersStatus !== 'loaded' || origin.missingOpenCashRegister}
                tooltip={origin.missingOpenCashRegister ? NO_OPEN_CASH_REGISTER_MESSAGE : undefined}
                tooltipOptions={{ position: 'top', showOnDisabled: true }}
            />
        </div>
    );

    return (
        <Dialog
            header={header}
            visible={visible}
            onHide={handleHide}
            footer={footer}
            style={{ width: '560px' }}
            breakpoints={{ '640px': '100vw' }}
            className="md-dialog"
            closable={!submitting}
            modal
        >
            {visible && (
                <div className="professional-payment">
                    <div className="md-readonly-grid">{summary}</div>
                    {note && <p className="md-help">{note}</p>}

                    {/* Mismo markup que el label de Combobox, para que los campos queden alineados */}
                    <div className="inputContainer flexible--column">
                        <label htmlFor="professional-payment-amount" className="label inputLabel">Monto a pagar *</label>
                        <InputNumber
                            inputId="professional-payment-amount"
                            inputRef={amountRef}
                            value={amount}
                            onValueChange={e => {
                                setAmount(e.value ?? null);
                                clearError('amount');
                            }}
                            mode="currency"
                            currency="ARS"
                            locale="es-AR"
                            minFractionDigits={2}
                            min={0}
                            disabled={submitting}
                            className={errors.amount ? 'field-error-target' : undefined}
                            aria-invalid={errors.amount ? true : undefined}
                            aria-describedby={errors.amount ? 'professional-payment-amount-error' : undefined}
                        />
                        <FieldError id="professional-payment-amount-error" message={errors.amount} />
                    </div>

                    <PaymentOriginFields
                        origin={origin}
                        paymentMethod={paymentMethod}
                        onPaymentMethodChange={setPaymentMethod}
                        errors={errors}
                        onFieldChange={field => {
                            clearError(field);
                            // Otra cuenta, otro saldo: el marcador del monto puede dejar de aplicar
                            if (field === 'financialAccount') clearError('amount');
                        }}
                        disabled={submitting}
                        idPrefix="professional-payment"
                        fieldClassName="professional-payment__field"
                        refs={{ paymentMethod: paymentMethodRef, cashRegister: cashRegisterRef, financialAccount: financialAccountRef }}
                    />
                    <PaymentOriginNote origin={origin} />
                </div>
            )}
        </Dialog>
    );
}
